import { useState, useMemo, useEffect } from "react";
import { useInfiniteScroll, post } from "@/lib/api";
import {
  finiteNumber,
  formatOperatorReason,
  formatUtcTime,
  formatUtcDateTime,
  formatIndianTime,
  formatIndianDate,
  formatIndianDateTime,
  formatDisplayTime,
  formatPriceWithCommas,
} from "../lib/operatorFormatters";
import InfiniteListFooter from "./InfiniteListFooter";
import {
  Radio,
  TrendingUp,
  TrendingDown,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  SlidersHorizontal,
  Sparkles,
  ChevronRight,
  Copy,
  Check,
  Eye,
  LayoutGrid,
  List,
  Table,
  Volume2,
  VolumeX,
  Zap,
  ShieldAlert,
  Gauge,
  Activity,
  Filter,
  Clock,
  Calendar,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  X,
  RotateCcw,
} from "lucide-react";

export default function SignalsFeed({
  signals: rawSignals = [],
  listState = {},
  selectedViewAccountId = null,
  selectedAccountId = null,
  onRefresh = null,
}) {
  const signals = useMemo(() => {
    if (Array.isArray(rawSignals)) return rawSignals;
    if (Array.isArray(rawSignals?.data)) return rawSignals.data;
    if (Array.isArray(rawSignals?.items)) return rawSignals.items;
    return [];
  }, [rawSignals]);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all, taken, rejected, high_conf, long, short
  const [reasonCategory, setReasonCategory] = useState("all"); // all, exposure, cooldown, reconciliation, min_conf, risk, ai
  const [minConfidence, setMinConfidence] = useState(0.0);
  const [viewMode, setViewMode] = useState("feed"); // feed, grid, audit
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [timezone, setTimezone] = useState(() => {
    try {
      return localStorage.getItem("operator_timezone") || "IST";
    } catch {
      return "IST";
    }
  });

  const handleTimezoneChange = (tz) => {
    setTimezone(tz);
    try {
      localStorage.setItem("operator_timezone", tz);
    } catch {}
  };

  const handleScroll = useInfiniteScroll(listState.loadMore || (() => {}), listState.hasMore);

  // Compute telemetry stats across loaded signals
  const stats = useMemo(() => {
    let taken = 0;
    let rejected = 0;
    let longs = 0;
    let shorts = 0;
    let highConf = 0;
    let totalConfSum = 0;

    signals.forEach((s) => {
      if (s.taken) taken++;
      else rejected++;

      if (s.side === "long") longs++;
      if (s.side === "short") shorts++;

      const conf = finiteNumber(s.confidence) || 0;
      if (conf >= 0.75) highConf++;
      totalConfSum += conf;
    });

    const total = signals.length;
    const avgConf = total > 0 ? (totalConfSum / total) : 0;
    const takenRate = total > 0 ? Math.round((taken / total) * 100) : 0;
    const longBias = total > 0 ? Math.round((longs / total) * 100) : 50;

    return {
      total,
      taken,
      rejected,
      longs,
      shorts,
      highConf,
      avgConf,
      takenRate,
      longBias,
    };
  }, [signals]);

  // Extract unique rejection categories present in the current dataset
  const rejectionCategories = useMemo(() => {
    const categories = new Set();
    signals.forEach((s) => {
      if (!s.taken && s.reject_reason) {
        const reasonStr = (s.reject_reason || "").toLowerCase();
        if (reasonStr.includes("exposure") || reasonStr.includes("position") || reasonStr.includes("concurrent")) {
          categories.add("exposure");
        } else if (reasonStr.includes("cooldown")) {
          categories.add("cooldown");
        } else if (reasonStr.includes("reconcil")) {
          categories.add("reconciliation");
        } else if (reasonStr.includes("confid") || reasonStr.includes("score") || reasonStr.includes("min")) {
          categories.add("min_conf");
        } else if (reasonStr.includes("ai") || reasonStr.includes("council")) {
          categories.add("ai");
        } else {
          categories.add("other");
        }
      }
    });
    return Array.from(categories);
  }, [signals]);

  // Filtered signals list based on search, tab, confidence, and rejection category
  const filteredSignals = useMemo(() => {
    return signals.filter((s) => {
      // Symbol search
      const sym = (s.symbol || "").toLowerCase();
      if (search && !sym.includes(search.toLowerCase().trim())) {
        return false;
      }

      // Main Filter tabs
      if (filter === "taken" && !s.taken) return false;
      if (filter === "rejected" && s.taken) return false;
      if (filter === "high_conf" && (s.confidence || 0) < 0.75) return false;
      if (filter === "long" && s.side !== "long") return false;
      if (filter === "short" && s.side !== "short") return false;

      // Min confidence slider
      if (minConfidence > 0 && (s.confidence || 0) < minConfidence) {
        return false;
      }

      // Rejection Reason Category Filter
      if (reasonCategory !== "all" && !s.taken) {
        const reasonStr = (s.reject_reason || "").toLowerCase();
        if (reasonCategory === "exposure") {
          if (!reasonStr.includes("exposure") && !reasonStr.includes("position") && !reasonStr.includes("concurrent")) return false;
        } else if (reasonCategory === "cooldown") {
          if (!reasonStr.includes("cooldown")) return false;
        } else if (reasonCategory === "reconciliation") {
          if (!reasonStr.includes("reconcil")) return false;
        } else if (reasonCategory === "min_conf") {
          if (!reasonStr.includes("confid") && !reasonStr.includes("score") && !reasonStr.includes("min")) return false;
        } else if (reasonCategory === "ai") {
          if (!reasonStr.includes("ai") && !reasonStr.includes("council")) return false;
        }
      }

      return true;
    });
  }, [signals, search, filter, minConfidence, reasonCategory]);

  const handleCopyJson = (sig, e) => {
    e?.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(sig, null, 2));
    setCopiedId(sig.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Humanize rejection explanation into operator-friendly guide
  const getRejectionGuide = (sig) => {
    if (sig.taken) return null;
    const r = (sig.reject_reason || "").toLowerCase();
    const label = sig.reject_reason_label || sig.reject_reason || "Not executed";

    if (r.includes("loss streak") || r.includes("consecutive losses")) {
      return {
        title: "Loss Streak Protection Active",
        desc: "Automated risk pause triggered after consecutive realized loss limit to protect portfolio capital.",
        fix: "Click 'Reset Loss Streak' below to clear consecutive losses and resume live entries.",
        severity: "alert",
        isLossStreak: true,
      };
    }
    if (r.includes("exposure") || r.includes("position") || r.includes("concurrent")) {
      return {
        title: "Max Exposure Guard Triggered",
        desc: "Portfolio heat or max active open positions limit reached. Risk Engine prevented entry to protect margin.",
        fix: "Close an active position or increase max concurrent trades in Settings.",
        severity: "warning",
      };
    }
    if (r.includes("cooldown")) {
      return {
        title: "Cooldown Guard Active",
        desc: "Symbol is in post-trade cooling period following recent exit/loss to prevent overtrading whipsaws.",
        fix: "Cooldown automatically expires after configured cooldown minutes.",
        severity: "info",
      };
    }
    if (r.includes("reconcil")) {
      return {
        title: "Exchange Reconciliation Lock",
        desc: "Local state is synchronizing with Binance exchange positions. Auto-heals in next scan.",
        fix: "Click Reconcile in overview if persistent.",
        severity: "alert",
      };
    }
    if (r.includes("min") || r.includes("confid") || r.includes("score")) {
      return {
        title: "Sub-Threshold Conviction",
        desc: `Signal confidence (${((sig.confidence || 0) * 100).toFixed(0)}%) did not meet minimum strategy threshold.`,
        fix: "Lower minimum signal confidence score threshold in Strategy settings if desired.",
        severity: "muted",
      };
    }
    return {
      title: label,
      desc: sig.reject_reason || "Execution gate did not approve order submission.",
      fix: "Check Risk Manager telemetry in dashboard.",
      severity: "muted",
    };
  };

  return (
    <div className="bg-zinc-950 border border-zinc-800/80 rounded-xl shadow-2xl flex flex-col h-full overflow-hidden" data-testid="signals-panel">
      {/* Top Telemetry KPI Bar */}
      <div className="p-3 bg-zinc-900/60 border-b border-zinc-800/80 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
        <div className="p-2.5 rounded-lg bg-zinc-950/80 border border-zinc-800/70 flex items-center justify-between">
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Signal Volume</div>
            <div className="text-base font-bold text-zinc-100 flex items-center gap-1.5 mt-0.5">
              <Zap size={14} className="text-emerald-400 animate-pulse" />
              {stats.total}
              <span className="text-[10px] text-emerald-400/80 font-normal">({stats.takenRate}% taken)</span>
            </div>
          </div>
        </div>

        <div className="p-2.5 rounded-lg bg-zinc-950/80 border border-zinc-800/70 flex items-center justify-between">
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Execution Split</div>
            <div className="text-sm font-bold mt-0.5 flex items-center gap-2">
              <span className="text-emerald-400 flex items-center gap-0.5">
                <CheckCircle2 size={12} /> {stats.taken}
              </span>
              <span className="text-zinc-600">/</span>
              <span className="text-rose-400 flex items-center gap-0.5">
                <XCircle size={12} /> {stats.rejected}
              </span>
            </div>
          </div>
          <div className="w-12 bg-zinc-900 h-2 rounded-full overflow-hidden border border-zinc-800 shrink-0">
            <div className="bg-emerald-500 h-full transition-all" style={{ width: `${stats.takenRate}%` }} />
          </div>
        </div>

        <div className="p-2.5 rounded-lg bg-zinc-950/80 border border-zinc-800/70">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Long / Short Bias</div>
          <div className="text-sm font-bold text-zinc-200 mt-0.5 flex items-center justify-between">
            <span className="text-emerald-400">{stats.longs} L</span>
            <div className="flex-1 mx-2 bg-rose-950/60 h-2 rounded-full overflow-hidden border border-zinc-800 relative">
              <div className="bg-emerald-500 h-full transition-all" style={{ width: `${stats.longBias}%` }} />
            </div>
            <span className="text-rose-400">{stats.shorts} S</span>
          </div>
        </div>

        <div className="p-2.5 rounded-lg bg-zinc-950/80 border border-zinc-800/70 flex items-center justify-between">
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">High Conviction</div>
            <div className="text-base font-bold text-cyan-400 flex items-center gap-1 mt-0.5">
              <Sparkles size={13} className="text-cyan-400" />
              {stats.highConf}
              <span className="text-[10px] text-zinc-500 font-normal">(&ge;0.75 conf)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Toolbar & Filter Header */}
      <div className="px-3.5 py-2.5 border-b border-zinc-800/70 flex flex-wrap items-center justify-between gap-2 bg-zinc-900/40">
        {/* Search & Rescan */}
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search symbol (e.g. BTC, ETH)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-7 py-1 text-xs bg-zinc-950 border border-zinc-800 rounded-lg text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-emerald-500/50 font-mono"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                <X size={12} />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`px-2.5 py-1 text-xs font-mono rounded-lg border flex items-center gap-1.5 transition-colors ${
              showAdvancedFilters || minConfidence > 0 || reasonCategory !== "all"
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800 hover:text-zinc-200"
            }`}
          >
            <SlidersHorizontal size={13} />
            <span>Filter</span>
            {(minConfidence > 0 || reasonCategory !== "all") && (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
            )}
          </button>
        </div>

        {/* View Mode & Filter Pills */}
        <div className="flex items-center gap-2">
          {/* Filter Pills */}
          <div className="flex items-center gap-1 text-[10px] font-mono">
            {[
              { id: "all", label: "All" },
              { id: "taken", label: `Taken (${stats.taken})` },
              { id: "rejected", label: `Rejections (${stats.rejected})` },
              { id: "high_conf", label: `High Conf (${stats.highConf})` },
              { id: "long", label: `Longs` },
              { id: "short", label: `Shorts` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-2 py-1 rounded-md transition-all ${
                  filter === tab.id
                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold shadow-sm shadow-emerald-950/80"
                    : "bg-zinc-900/80 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 border border-zinc-800/60"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Timezone Switcher */}
          <div className="flex items-center bg-zinc-950 border border-zinc-800 p-0.5 rounded-lg text-[10px] font-mono shadow-inner">
            <button
              onClick={() => handleTimezoneChange("IST")}
              title="Indian Standard Time (UTC+05:30)"
              className={`px-2 py-0.5 rounded transition-all font-semibold flex items-center gap-1 ${
                timezone === "IST"
                  ? "bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <span>🇮🇳 IST</span>
            </button>
            <button
              onClick={() => handleTimezoneChange("UTC")}
              title="Coordinated Universal Time (UTC)"
              className={`px-2 py-0.5 rounded transition-all font-semibold flex items-center gap-1 ${
                timezone === "UTC"
                  ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <span>🌐 UTC</span>
            </button>
          </div>

          {/* View Mode Switcher */}
          <div className="flex items-center bg-zinc-950 border border-zinc-800 p-0.5 rounded-lg text-zinc-400">
            <button
              onClick={() => setViewMode("feed")}
              title="Stream List View"
              className={`p-1 rounded ${viewMode === "feed" ? "bg-zinc-800 text-emerald-400" : "hover:text-zinc-200"}`}
            >
              <List size={14} />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              title="Visual Card Grid"
              className={`p-1 rounded ${viewMode === "grid" ? "bg-zinc-800 text-emerald-400" : "hover:text-zinc-200"}`}
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => setViewMode("audit")}
              title="Audit Table View"
              className={`p-1 rounded ${viewMode === "audit" ? "bg-zinc-800 text-emerald-400" : "hover:text-zinc-200"}`}
            >
              <Table size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Expandable Advanced Filter Panel */}
      {showAdvancedFilters && (
        <div className="px-3.5 py-2.5 bg-zinc-900/80 border-b border-zinc-800/80 flex flex-wrap items-center justify-between gap-4 text-xs font-mono">
          <div className="flex items-center gap-3">
            <span className="text-zinc-400 font-bold flex items-center gap-1">
              <Filter size={12} /> Min Confidence:
            </span>
            <input
              type="range"
              min="0.50"
              max="0.95"
              step="0.05"
              value={minConfidence}
              onChange={(e) => setMinConfidence(parseFloat(e.target.value))}
              className="w-28 accent-emerald-500 cursor-pointer"
            />
            <span className="text-emerald-400 font-bold bg-zinc-950 px-1.5 py-0.5 rounded border border-zinc-800">
              {minConfidence > 0 ? `${(minConfidence * 100).toFixed(0)}%+` : "Any"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-zinc-400 font-bold">Rejection Category:</span>
            <select
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-300 focus:outline-none focus:border-emerald-500/50"
            >
              <option value="all">All Rejection Types</option>
              <option value="exposure">Max Exposure Limit</option>
              <option value="cooldown">Cooldown Active</option>
              <option value="reconciliation">Reconciliation Lock</option>
              <option value="min_conf">Sub-Threshold Score</option>
              <option value="ai">AI Council Rejection</option>
            </select>
          </div>

          {(minConfidence > 0 || reasonCategory !== "all" || search) && (
            <button
              onClick={() => {
                setMinConfidence(0);
                setReasonCategory("all");
                setSearch("");
                setFilter("all");
              }}
              className="text-[11px] text-rose-400 hover:text-rose-300 hover:underline"
            >
              Reset Filters
            </button>
          )}
        </div>
      )}

      {/* Content Feed Section */}
      <div className="infinite-scroll-container flex-1 overflow-y-auto max-h-[580px]" onScroll={handleScroll}>
        {filteredSignals.length === 0 ? (
          <div className="px-4 py-16 text-center space-y-2 font-mono" data-testid="no-signals">
            <div className="w-10 h-10 mx-auto rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-600">
              <Radio size={18} className="animate-pulse" />
            </div>
            <div className="text-xs text-zinc-400 font-bold">No Signals Match Criteria</div>
            <div className="text-[11px] text-zinc-600 max-w-sm mx-auto">
              Market scanner is scanning 300+ pairs in background. Adjust filter sliders or wait for next evaluation cycle.
            </div>
          </div>
        ) : viewMode === "grid" ? (
          /* Grid View Mode */
          <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2.5 font-mono">
            {filteredSignals.map((s) => (
              <SignalGridCard
                key={s.id}
                signal={s}
                timezone={timezone}
                onInspect={() => setSelectedSignal(s)}
                onCopy={(e) => handleCopyJson(s, e)}
                isCopied={copiedId === s.id}
              />
            ))}
          </div>
        ) : viewMode === "audit" ? (
          /* Audit Table View Mode */
          <div className="overflow-x-auto font-mono text-xs">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-zinc-900/80 text-zinc-400 border-b border-zinc-800 text-[10px] uppercase tracking-wider">
                  <th className="p-2.5">Time ({timezone})</th>
                  <th className="p-2.5">Symbol</th>
                  <th className="p-2.5">Side</th>
                  <th className="p-2.5">Price</th>
                  <th className="p-2.5">Conf</th>
                  <th className="p-2.5">Status</th>
                  <th className="p-2.5">Diagnostic / Guard Reason</th>
                  <th className="p-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-900">
                {filteredSignals.map((s) => {
                  const isLong = s.side === "long";
                  const isTaken = Boolean(s.taken);
                  const price = finiteNumber(s.price);
                  const confPct = Math.round((s.confidence || 0) * 100);

                  return (
                    <tr
                      key={s.id}
                      onClick={() => setSelectedSignal(s)}
                      className="hover:bg-zinc-900/50 cursor-pointer transition-colors text-zinc-300"
                    >
                      <td className="p-2.5 text-[10px] whitespace-nowrap">
                        <div className="font-semibold text-amber-300/90">{formatDisplayTime(s.ts, { timezone })}</div>
                        <div className="text-[9px] text-zinc-500">
                          {timezone === "IST" ? formatIndianDate(s.ts, { includeYear: false }) : formatUtcTime(s.ts)}
                        </div>
                      </td>
                      <td className="p-2.5 font-bold text-zinc-100">{s.symbol?.replace(":USDT", "")}</td>
                      <td className="p-2.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isLong ? "text-emerald-400 bg-emerald-950/60" : "text-rose-400 bg-rose-950/60"}`}>
                          {s.side}
                        </span>
                      </td>
                      <td className="p-2.5 font-mono">@{formatPriceWithCommas(price)}</td>
                      <td className="p-2.5">
                        <span className={`font-bold ${confPct >= 75 ? "text-emerald-400" : confPct >= 60 ? "text-cyan-400" : "text-amber-400"}`}>
                          {confPct}%
                        </span>
                      </td>
                      <td className="p-2.5">
                        {isTaken ? (
                          <span className="text-emerald-400 font-bold flex items-center gap-1">
                            <CheckCircle2 size={11} /> TAKEN
                          </span>
                        ) : (
                          <span className="text-rose-400 font-bold flex items-center gap-1">
                            <XCircle size={11} /> REJECTED
                          </span>
                        )}
                      </td>
                      <td className="p-2.5 text-[11px] text-zinc-400" title={s.reject_reason}>
                        {isTaken ? "Executed" : formatOperatorReason(s.reject_reason_label, s.reject_reason, "Not executed")}
                      </td>
                      <td className="p-2.5 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSignal(s);
                          }}
                          className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-zinc-200"
                        >
                          <Eye size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Stream List View Mode (Enhanced Default) */
          <div className="divide-y divide-zinc-900/80">
            {filteredSignals.map((s) => (
              <SignalListItem
                key={s.id}
                signal={s}
                timezone={timezone}
                onInspect={() => setSelectedSignal(s)}
                onCopy={(e) => handleCopyJson(s, e)}
                isCopied={copiedId === s.id}
                rejectionGuide={getRejectionGuide(s)}
                selectedAccountId={selectedViewAccountId || selectedAccountId}
                onReset={onRefresh}
              />
            ))}
          </div>
        )}

        {!!signals?.length && (
          <InfiniteListFooter
            loadingMore={listState.loadingMore}
            hasMore={listState.hasMore}
            error={listState.error}
          />
        )}
      </div>

      {/* Signal Detail & Rejection Inspector Modal */}
      {selectedSignal && (
        <SignalInspectorModal
          signal={selectedSignal}
          timezone={timezone}
          onClose={() => setSelectedSignal(null)}
          guide={getRejectionGuide(selectedSignal)}
          selectedAccountId={selectedViewAccountId || selectedAccountId}
          onReset={onRefresh}
        />
      )}
    </div>
  );
}

/* Sub-component: Stream List Item */
function SignalListItem({
  signal: s,
  timezone = "IST",
  onInspect,
  onCopy,
  isCopied,
  rejectionGuide,
  selectedAccountId,
  onReset,
}) {
  const [resettingItem, setResettingItem] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const isLong = s.side === "long";
  const isTaken = Boolean(s.taken);
  const cleanSym = (s.symbol || "").replace(":USDT", "").replace("/USDT", "");
  const price = finiteNumber(s.price);
  const confPct = Math.round((s.confidence || 0) * 100);

  const handleQuickReset = async (e) => {
    e.stopPropagation();
    setResettingItem(true);
    try {
      const targetAccount = selectedAccountId || "paper";
      await post(`/accounts/${encodeURIComponent(targetAccount)}/reset-loss-streak`);
      setResetDone(true);
      if (onReset) onReset();
      setTimeout(() => setResetDone(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setResettingItem(false);
    }
  };

  // Indicators calculations if present
  const rsi = finiteNumber(s.rsi);
  const atr = finiteNumber(s.atr);
  const volRatio = finiteNumber(s.volume_ratio);

  return (
    <div
      onClick={onInspect}
      className={`p-3.5 hover:bg-zinc-900/70 cursor-pointer transition-all space-y-2 group border-l-2 ${
        isTaken
          ? "border-emerald-500 hover:border-emerald-400 bg-emerald-950/15"
          : isLong
          ? "border-emerald-500/40 hover:border-emerald-400/80 bg-zinc-950/60"
          : "border-rose-500/40 hover:border-rose-400/80 bg-zinc-950/60"
      }`}
      data-testid={`signal-item-${s.id}`}
    >
      <div className="flex items-center justify-between text-xs font-mono">
        <div className="flex items-center gap-2">
          <span className="font-bold text-sm text-zinc-100 group-hover:text-emerald-300 transition-colors">{cleanSym}</span>
          <span className="text-[9px] text-zinc-400 bg-zinc-900 px-1.5 py-0.5 rounded border border-zinc-800">USDT</span>
          <span className="text-xs text-zinc-300 font-semibold">@{formatPriceWithCommas(price)}</span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider shadow-sm ${
              isLong
                ? "bg-emerald-950/90 text-emerald-400 border border-emerald-800/80 shadow-emerald-950/50"
                : "bg-rose-950/90 text-rose-400 border border-rose-800/80 shadow-rose-950/50"
            }`}
          >
            {isLong ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {s.side}
          </span>

          <span className="text-[10px] text-zinc-300 flex items-center gap-1 bg-zinc-900 px-2 py-0.5 rounded border border-zinc-800 font-medium">
            <Sparkles size={10} className={confPct >= 75 ? "text-cyan-400" : "text-amber-400"} />
            <span>{confPct}%</span>
          </span>
        </div>
      </div>

      {/* Confidence Bar & Indicators Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] font-mono">
        {/* Indicators Pill Group */}
        <div className="flex items-center gap-1.5 flex-wrap text-zinc-400">
          {rsi != null && (
            <span
              className={`px-1.5 py-0.5 rounded border font-medium ${
                rsi < 35
                  ? "bg-emerald-950/70 text-emerald-300 border-emerald-800/70"
                  : rsi > 65
                  ? "bg-rose-950/70 text-rose-300 border-rose-800/70"
                  : "bg-zinc-900 text-zinc-400 border-zinc-800"
              }`}
            >
              RSI: {rsi.toFixed(1)}
            </span>
          )}

          {atr != null && (
            <span className="px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-400 border border-zinc-800">
              ATR: {atr.toFixed(4)}
            </span>
          )}

          {volRatio != null && (
            <span className="px-1.5 py-0.5 rounded bg-zinc-900 text-cyan-300 border border-cyan-900/50 font-medium">
              Vol: {volRatio.toFixed(1)}x
            </span>
          )}
        </div>

        {/* Status / Rejection Tag - UNTRUNCATED responsive badge */}
        <div className="shrink-0 flex items-center gap-1.5">
          {isTaken ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[10px] font-bold bg-emerald-950/80 text-emerald-400 border border-emerald-800/70 shadow-sm shadow-emerald-950">
              <CheckCircle2 size={12} /> EXECUTED
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-[10px] font-medium bg-rose-950/50 text-rose-300 border border-rose-900/70 shadow-sm"
              title={s.reject_reason}
            >
              <XCircle size={12} className="shrink-0 text-rose-400" />
              <span>{formatOperatorReason(s.reject_reason_label, s.reject_reason, "Not executed")}</span>
            </span>
          )}
          {rejectionGuide?.isLossStreak && (
            <button
              data-testid={`reset-streak-item-btn-${s.id}`}
              onClick={handleQuickReset}
              disabled={resettingItem}
              title="Click to reset loss streak and resume live entries"
              className="px-2 py-0.5 rounded bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-[10px] font-bold text-white border border-rose-400/40 flex items-center gap-1 shadow transition-all cursor-pointer font-mono"
            >
              <RotateCcw size={10} className={resettingItem ? "animate-spin" : ""} />
              {resettingItem ? "..." : resetDone ? "✓ Reset" : "Reset"}
            </button>
          )}
        </div>
      </div>

      {/* Footer Audit Row */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-[9px] font-mono text-zinc-500 pt-1.5 border-t border-zinc-900/80">
        <div className="flex items-center gap-2 flex-wrap">
          {timezone === "IST" ? (
            <>
              <span className="text-amber-300 font-semibold flex items-center gap-1">
                <Clock size={10} className="text-amber-400" />
                {formatIndianTime(s.ts)}
              </span>
              <span className="text-zinc-600">&middot;</span>
              <span className="text-zinc-400 flex items-center gap-1">
                <Calendar size={10} className="text-zinc-500" />
                {formatIndianDate(s.ts)}
              </span>
              <span className="text-zinc-500 font-mono text-[8px] bg-zinc-900/90 px-1 py-0.5 rounded border border-zinc-800" title="Exchange UTC Time">
                {formatUtcTime(s.ts)}
              </span>
            </>
          ) : (
            <>
              <span className="text-cyan-300 font-semibold flex items-center gap-1">
                <Clock size={10} className="text-cyan-400" />
                {formatUtcTime(s.ts)}
              </span>
              <span className="text-zinc-600">&middot;</span>
              <span className="text-zinc-500 font-mono text-[8px] bg-zinc-900/90 px-1 py-0.5 rounded border border-zinc-800" title="Indian Standard Time">
                IST: {formatIndianTime(s.ts)}
              </span>
            </>
          )}
          <span className="text-zinc-600">&middot;</span>
          <span className="uppercase text-zinc-500 font-medium tracking-wider">{s.regime || "trending"}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopy();
            }}
            title="Copy Signal JSON"
            className="hover:text-zinc-200 p-1 rounded hover:bg-zinc-800/60 transition-colors"
          >
            {isCopied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
          </button>
          <span className="flex items-center gap-0.5 text-emerald-400/90 group-hover:translate-x-0.5 transition-transform font-medium">
            Details <ChevronRight size={10} />
          </span>
        </div>
      </div>
    </div>
  );
}

/* Sub-component: Signal Grid Card */
function SignalGridCard({ signal: s, timezone = "IST", onInspect, onCopy, isCopied }) {
  const isLong = s.side === "long";
  const isTaken = Boolean(s.taken);
  const cleanSym = (s.symbol || "").replace(":USDT", "").replace("/USDT", "");
  const price = finiteNumber(s.price);
  const confPct = Math.round((s.confidence || 0) * 100);

  return (
    <div
      onClick={onInspect}
      className={`p-3.5 rounded-xl border bg-zinc-950/90 hover:bg-zinc-900/80 cursor-pointer transition-all space-y-2 flex flex-col justify-between ${
        isTaken
          ? "border-emerald-500/50 shadow-lg shadow-emerald-950/30"
          : isLong
          ? "border-zinc-800 hover:border-emerald-500/40"
          : "border-zinc-800 hover:border-rose-500/40"
      }`}
    >
      <div className="flex items-center justify-between text-xs font-mono">
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-zinc-100 text-sm">{cleanSym}</span>
          <span className="text-[9px] text-zinc-400 bg-zinc-900 px-1 py-0.5 rounded border border-zinc-800">USDT</span>
        </div>
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
            isLong ? "bg-emerald-950/80 text-emerald-400 border border-emerald-800/60" : "bg-rose-950/80 text-rose-400 border border-rose-800/60"
          }`}
        >
          {s.side}
        </span>
      </div>

      <div className="space-y-1.5 font-mono">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <span>Price:</span>
          <span className="font-bold text-zinc-200">@{formatPriceWithCommas(price)}</span>
        </div>

        {/* Confidence Progress Bar */}
        <div className="space-y-0.5">
          <div className="flex justify-between text-[10px] text-zinc-500">
            <span>Confidence</span>
            <span className="font-bold text-cyan-400">{confPct}%</span>
          </div>
          <div className="w-full bg-zinc-900 h-1.5 rounded-full overflow-hidden border border-zinc-800">
            <div
              className={`h-full transition-all ${
                confPct >= 75 ? "bg-emerald-400" : confPct >= 60 ? "bg-cyan-400" : "bg-amber-400"
              }`}
              style={{ width: `${confPct}%` }}
            />
          </div>
        </div>

        {/* Timestamp strip */}
        <div className="flex items-center justify-between text-[9px] text-zinc-500 pt-1">
          <span className="text-amber-400/90 font-medium flex items-center gap-1">
            <Clock size={10} className="text-amber-400" />
            {formatDisplayTime(s.ts, { timezone })}
          </span>
          <span>{formatIndianDate(s.ts, { includeYear: false })}</span>
        </div>
      </div>

      <div className="pt-2 border-t border-zinc-900 flex items-center justify-between text-[10px] gap-2 font-mono">
        {isTaken ? (
          <span className="text-emerald-400 font-bold flex items-center gap-1">
            <CheckCircle2 size={11} /> TAKEN
          </span>
        ) : (
          <span className="text-rose-400 font-medium truncate" title={s.reject_reason}>
            {formatOperatorReason(s.reject_reason_label, s.reject_reason, "Not executed")}
          </span>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onCopy();
          }}
          className="text-zinc-500 hover:text-zinc-300 shrink-0"
        >
          {isCopied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
        </button>
      </div>
    </div>
  );
}

/* Sub-component: Signal Detail & Rejection Inspector Modal */
function SignalInspectorModal({
  signal: s,
  timezone = "IST",
  onClose,
  guide,
  selectedAccountId,
  onReset,
}) {
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const isLong = s.side === "long";
  const isTaken = Boolean(s.taken);
  const price = finiteNumber(s.price);
  const confPct = Math.round((s.confidence || 0) * 100);

  const handleResetStreak = async () => {
    setResetting(true);
    try {
      const targetAccount = selectedAccountId || "paper";
      await post(`/accounts/${encodeURIComponent(targetAccount)}/reset-loss-streak`);
      setResetSuccess(true);
      if (onReset) onReset();
      setTimeout(() => setResetSuccess(false), 3500);
    } catch (err) {
      console.error("Failed to reset loss streak:", err);
    } finally {
      setResetting(false);
    }
  };

  // Compute calculated SL/TP targets based on price if ATR present
  const atr = finiteNumber(s.atr);
  const stopLossTarget = price && atr ? (isLong ? price - atr * 1.5 : price + atr * 1.5) : null;
  const takeProfitTarget = price && atr ? (isLong ? price + atr * 3.0 : price - atr * 3.0) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl space-y-0 font-mono">
        {/* Header */}
        <div className="p-4 border-b border-zinc-800/80 flex items-center justify-between bg-zinc-900/60">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold ${isLong ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-rose-500/20 text-rose-400 border border-rose-500/40"}`}>
              {isLong ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-zinc-100">{s.symbol}</h3>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isLong ? "bg-emerald-950 text-emerald-400 border border-emerald-800" : "bg-rose-950 text-rose-400 border border-rose-800"}`}>
                  {s.side}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap text-[10px] mt-0.5">
                <span className="text-zinc-400">Signal #{s.id}</span>
                <span className="text-zinc-600">&middot;</span>
                <span className="text-amber-400 font-semibold flex items-center gap-1">
                  <Clock size={11} /> {formatIndianDateTime(s.ts)}
                </span>
                <span className="text-zinc-500 font-mono text-[9px] bg-zinc-900 px-1.5 py-0.2 rounded border border-zinc-800">
                  {formatUtcTime(s.ts)}
                </span>
              </div>
            </div>
          </div>

          <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Timezone Audit Card */}
          <div className="p-3 rounded-xl bg-zinc-900/60 border border-zinc-800/80 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-1">
                <Clock size={11} className="text-amber-400" /> Indian Standard Time (IST)
              </span>
              <div className="text-xs font-bold text-amber-300 mt-1">
                {formatIndianDateTime(s.ts)}
              </div>
            </div>
            <div>
              <span className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-1">
                <Globe size={11} className="text-cyan-400" /> Exchange UTC Time
              </span>
              <div className="text-xs font-mono text-zinc-300 mt-1">
                {formatUtcDateTime(s.ts)}
              </div>
            </div>
          </div>
          {/* Status Diagnostic Card */}
          {isTaken ? (
            <div className="p-3 rounded-xl bg-emerald-950/30 border border-emerald-500/30 flex items-center gap-3 text-emerald-300">
              <CheckCircle2 size={20} className="shrink-0 text-emerald-400" />
              <div>
                <h4 className="text-xs font-bold uppercase">Order Executed Successfully</h4>
                <p className="text-[11px] text-emerald-400/80 mt-0.5">
                  Signal met all strategy thresholds &amp; risk manager limits. Position opened on exchange.
                </p>
              </div>
            </div>
          ) : guide ? (
            <div className="p-3 rounded-xl bg-rose-950/30 border border-rose-500/30 space-y-2 text-rose-200">
              <div className="flex items-center gap-2 text-rose-400 font-bold text-xs uppercase">
                <ShieldAlert size={16} />
                {guide.title}
              </div>
              <p className="text-[11px] text-rose-300/90 leading-relaxed">{guide.desc}</p>
              <div className="pt-2 border-t border-rose-900/50 flex flex-wrap items-center justify-between gap-2">
                <div className="text-[10px] text-amber-300 flex items-center gap-1.5">
                  <Info size={12} className="shrink-0 text-amber-400" />
                  <span><strong>Operator Tip:</strong> {guide.fix}</span>
                </div>
                {guide.isLossStreak && (
                  <button
                    data-testid="modal-reset-loss-streak-btn"
                    onClick={handleResetStreak}
                    disabled={resetting}
                    className="mt-1 sm:mt-0 px-3.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-lg shadow-rose-950/60 border border-rose-400/40 transition-all font-mono uppercase tracking-wider cursor-pointer"
                  >
                    <RotateCcw size={12} className={resetting ? "animate-spin" : ""} />
                    {resetting ? "Resetting Streak..." : resetSuccess ? "✅ Streak Reset & Resumed!" : "Reset Loss Streak & Resume"}
                  </button>
                )}
              </div>
            </div>
          ) : null}

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800/60">
              <span className="text-[10px] text-zinc-500 uppercase font-bold">Signal Entry Price</span>
              <div className="text-sm font-bold text-zinc-100 mt-0.5">@{price == null ? "—" : price.toFixed(4)}</div>
            </div>

            <div className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800/60">
              <span className="text-[10px] text-zinc-500 uppercase font-bold">Confidence Score</span>
              <div className="text-sm font-bold text-cyan-400 mt-0.5">{confPct}% Conviction</div>
            </div>

            {stopLossTarget && (
              <div className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800/60">
                <span className="text-[10px] text-rose-400 uppercase font-bold">Suggested Stop Loss</span>
                <div className="text-xs font-bold text-rose-300 mt-0.5">@{stopLossTarget.toFixed(4)}</div>
              </div>
            )}

            {takeProfitTarget && (
              <div className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800/60">
                <span className="text-[10px] text-emerald-400 uppercase font-bold">Suggested Take Profit</span>
                <div className="text-xs font-bold text-emerald-300 mt-0.5">@{takeProfitTarget.toFixed(4)}</div>
              </div>
            )}
          </div>

          {/* Strategy Trigger Reasons */}
          {Array.isArray(s.reasons) && s.reasons.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Triggered Technical Factors</h4>
              <div className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800/60 space-y-1 text-xs text-zinc-300">
                {s.reasons.map((reason, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                    <span>{reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Telemetry Data */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[10px] text-zinc-500">
              <span className="uppercase font-bold">Telemetry Data Payload</span>
              <button
                onClick={(e) => {
                  navigator.clipboard.writeText(JSON.stringify(s, null, 2));
                }}
                className="text-emerald-400 hover:underline flex items-center gap-1"
              >
                <Copy size={10} /> Copy Payload
              </button>
            </div>
            <pre className="p-2.5 rounded-xl bg-black border border-zinc-900 text-[10px] text-emerald-400/80 overflow-x-auto max-h-32">
              {JSON.stringify(s, null, 2)}
            </pre>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-3 border-t border-zinc-800/80 bg-zinc-900/60 flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-zinc-200 transition-colors">
            Close Inspector
          </button>
        </div>
      </div>
    </div>
  );
}
